import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import logger from './logger'

// API Response wrapper
export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
}

// API path prefix
const API_PREFIX = '/sequence/api/v1'

// Create axios instance
const http: AxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}${API_PREFIX}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token to headers
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage or other storage
    const token = localStorage.getItem('auth_token')

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    logger.error('Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - Handle response data and errors
http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response

    // Handle successful response
    if (data.success === true || response.status === 200) {
      return response
    }

    // Handle business error
    logger.error('Business error:', data.message)
    return Promise.reject(new Error(data.message || 'Request failed'))
  },
  (error) => {
    // Handle network error or server error
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('auth_token')
          logger.error('Unauthorized, please login again')
          break
        case 403:
          logger.error('Access forbidden')
          break
        case 404:
          logger.error('Resource not found')
          break
        case 500:
          logger.error('Internal server error')
          break
        default:
          logger.error(`Request failed: ${data?.message || error.message}`)
      }
    } else if (error.request) {
      logger.error('Network error, please check your connection')
    } else {
      logger.error('Request error:', error.message)
    }

    return Promise.reject(error)
  }
)

// Generic request method
export const request = <T = any>(config: AxiosRequestConfig): Promise<T> => {
  return http.request<ApiResponse<T>>(config).then((response) => response.data.data as T)
}

// Convenience methods
export const get = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return request<T>({ ...config, method: 'GET', url })
}

export const post = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request<T>({ ...config, method: 'POST', url, data })
}

export const put = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request<T>({ ...config, method: 'PUT', url, data })
}

export const del = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return request<T>({ ...config, method: 'DELETE', url })
}

export default http
